import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { UserRole } from "@/lib/schema";
import { useLocation } from "wouter";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

interface SuperAdminProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function SuperAdminProtectedRoute({ children, fallback }: SuperAdminProtectedRouteProps) {
  const { currentUser, userData, loading } = useAuth();
  const [_, setLocation] = useLocation();

  // Handle navigation when user is not authenticated
  useEffect(() => {
    if (!loading && (!currentUser || !userData)) {
      setLocation("/");
    }
  }, [loading, currentUser, userData, setLocation]);

  // Handle navigation when user doesn't have super admin role
  useEffect(() => {
    if (!loading && currentUser && userData && userData.role === UserRole.USER) {
      setLocation("/dashboard");
    }
  }, [loading, currentUser, userData, setLocation]);

  if (loading) {
    return <LoadingScreen text="Loading..." />;
  }

  if (!currentUser || !userData) {
    return null;
  }

  const hasAccess = userData.role === UserRole.SUPER_ADMIN || userData.role === UserRole.RECRUITER;

  if (!hasAccess) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Super Admin Access Required</h1>
          <p className="text-slate-600">You need super admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
